'use client';
import Image from 'next/image';

import { IExperienceDictionary } from '@/dictionaries/pages/experience/types';
import {
  ExperienceMedia as ExperienceMediaService,
  ExperienceVideoPreviewImage,
} from '@/services/experienceTypes';
import getMediaFilterUrl from '@/utils/get-media-filter';

import GalleryModalImages from './GalleryModalImages';
import VideoModal from './VideoModal';

type Props = {
  media: ExperienceMediaService[];
  dict: IExperienceDictionary;
  videoUrl: string | null;
  videoPreviewImage: ExperienceVideoPreviewImage | null;
};

export default function ExperienceMedia({
  media,
  dict,
  videoUrl,
  videoPreviewImage,
}: Props) {
  const coverMedia = media.filter((medias) => medias.type == 'cover')[0];
  const experienceImgCover = getMediaFilterUrl({
    mediaFilters: coverMedia.mediaFilters,
    filterName: '1920w_webp',
  });

  const experienceMedia = media.filter((medias) => medias.type !== 'video');

  const experienceVideoCover =
    videoPreviewImage &&
    getMediaFilterUrl({
      mediaFilters: videoPreviewImage.mediaFilters,
      filterName: 'large_3-2_webp',
    });

  const galleryMedia = media.filter((medias) => medias.type == 'gallery')[0];
  const galleryImgCover = `${galleryMedia?.path}${galleryMedia?.filename}`;

  const hasVideo = videoUrl != null ? true : false;

  return (
    <div
      id="media"
      className="w-full px-sm md:px-md desktop:px-lg max-w-default mx-auto lg:mx-0 desktop:max-w-full">
      <div className="mb-[30px] sm:mb-[50px] desktop:mb-[80px]">
        <div className="mx-[-0.625rem] flex sm:mx-[-0.9375rem]">
          <div className="flex-[0_0_auto] w-full sm:w-[calc(100%-1.875rem)] sm:mx-[0.9375rem]">
            {hasVideo ? (
              <>
                <div className="hidden desktop:flex w-full gap-[30px]">
                  <div className="w-[calc(66.66667%)] relative min-h-[550px] max-h-[900px]">
                    <Image
                      src={experienceImgCover ?? ''}
                      alt="Photo cover experience"
                      fill
                      className="opacity-100 transition-all duration-300 ease-in-out w-full h-full object-cover object-[50%_50%] inline-block align-middle max-w-full"
                      sizes="(max-width: 480px) 100vw, (max-width: 767px) 90vw, (max-width: 1280px) 80vw, 100vw"
                    />
                    <GalleryModalImages
                      dict={dict}
                      experienceMedia={experienceMedia}
                      hasVideo={hasVideo}
                      hideTexts
                      videoUrl={videoUrl}
                    />
                  </div>

                  <div className="flex flex-col w-[calc(33.33333%-1.875rem)] gap-[30px]">
                    <div className="relative h-[50%]">
                      <Image
                        src={
                          videoPreviewImage
                            ? experienceVideoCover ?? ''
                            : `${process.env.NEXT_PUBLIC_S3_URI}/assets/images/placeholder.jpg`
                        }
                        alt="Photo video experience"
                        fill
                        className="opacity-100 transition-all duration-300 ease-in-out w-full h-full object-cover object-[50%_50%] inline-block align-middle max-w-full bg-black"
                        sizes="(max-width: 480px) 100vw, (max-width: 767px) 90vw, (max-width: 1280px) 80vw, 100vw"
                        loading="lazy"
                      />
                      <VideoModal videoUrl={videoUrl} />
                    </div>
                    <div className="relative h-[50%]">
                      <Image
                        src={`${process.env.NEXT_PUBLIC_S3_URI}/thumbs/large_3-2_webp/${galleryImgCover}`}
                        alt="Photo cover experience"
                        fill
                        className="blur-[0.5px] opacity-100 transition-all duration-300 ease-in-out w-full h-full object-cover object-[50%_50%] inline-block align-middle max-w-ful"
                        sizes="(max-width: 480px) 100vw, (max-width: 767px) 90vw, (max-width: 1280px) 80vw, 100vw"
                        loading="lazy"
                      />
                      <GalleryModalImages
                        dict={dict}
                        experienceMedia={experienceMedia}
                        hasVideo={hasVideo}
                        videoUrl={videoUrl}
                        hasDarkBg
                      />
                    </div>
                  </div>
                </div>
                <div className="desktop:hidden mx-[-0.9375rem] relative min-h-[550px] h-[60vh] max-h-[900px] sm:mx-[-2.5rem] desktop:mx-0">
                  <Image
                    src={experienceImgCover ?? ''}
                    alt="Photo cover experience"
                    fill
                    className="opacity-100 transition-all duration-300 ease-in-out w-full h-full object-cover object-[50%_50%] inline-block align-middle max-w-full"
                    sizes="(max-width: 480px) 100vw, (max-width: 767px) 90vw, (max-width: 1280px) 80vw, 100vw"
                    priority
                  />
                  <VideoModal videoUrl={videoUrl} />
                  <GalleryModalImages
                    dict={dict}
                    experienceMedia={experienceMedia}
                    hasVideo={false}
                    videoUrl={videoUrl}
                  />
                </div>
              </>
            ) : (
              <div className="mx-[-0.9375rem] relative min-h-[550px] h-[60vh] max-h-[900px] sm:mx-[-2.5rem] desktop:mx-0">
                <Image
                  src={experienceImgCover ?? ''}
                  alt="Photo cover experience"
                  fill
                  className="opacity-100 transition-all duration-300 ease-in-out w-full h-full object-cover object-[50%_50%] inline-block align-middle max-w-full"
                  sizes="(max-width: 480px) 100vw, (max-width: 767px) 90vw, (max-width: 1280px) 80vw, 100vw"
                  priority
                />
                <GalleryModalImages
                  dict={dict}
                  experienceMedia={experienceMedia}
                  hasVideo={hasVideo}
                  videoUrl={videoUrl}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
